import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import type { DrivingLimitStatus } from '../api/apiService';
import { formatLimitHours } from './drivingLimit';

// Local notifications for the rolling 24h driving limit (Lei 45/2018, art. 13).
// Production runs no Celery beat, so the server cannot push when a threshold is
// crossed mid-drive; the app schedules them from the server's projection and
// reschedules on every status refresh.
export const DRIVING_LIMIT_WARNING_NOTIFICATION_ID = 'driving-limit-warning';
export const DRIVING_LIMIT_REACHED_NOTIFICATION_ID = 'driving-limit-reached';


const cancelById = async (identifier: string) => {
  try {
    await Notifications.cancelScheduledNotificationAsync(identifier);
  } catch (error) {
    console.warn('Failed to cancel driving limit notification', { identifier, error });
  }
};

export const cancelDrivingLimitNotifications = async () => {
  await Promise.all([
    cancelById(DRIVING_LIMIT_WARNING_NOTIFICATION_ID),
    cancelById(DRIVING_LIMIT_REACHED_NOTIFICATION_ID),
  ]);
};

const schedule = async (identifier: string, seconds: number, title: string, body: string) => {
  try {
    await Notifications.scheduleNotificationAsync({
      identifier,
      content: { title, body, sound: true },
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.TIME_INTERVAL,
        seconds: Math.max(1, Math.round(seconds)),
        repeats: false,
      },
    });
  } catch (error) {
    console.warn('Failed to schedule driving limit notification', { identifier, error });
  }
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

const applyStatus = async (
  limit: DrivingLimitStatus | null | undefined,
  isDriving: boolean,
  t: Translate,
  language?: string,
) => {
  await cancelDrivingLimitNotifications();
  if (!limit || !isDriving || !limit.is_driving) return;

  const hours = formatLimitHours(limit.limit_seconds, language);

  if (limit.seconds_until_warning != null && limit.seconds_until_warning > 0) {
    // Time from the warning to the limit; with a rolling window it can exceed
    // the configured lead because older driving leaves the window meanwhile.
    const minutes = Math.round(
      ((limit.seconds_until_limit ?? limit.seconds_until_warning + limit.warning_lead_seconds) -
        limit.seconds_until_warning) / 60,
    );
    await schedule(
      DRIVING_LIMIT_WARNING_NOTIFICATION_ID,
      limit.seconds_until_warning,
      t('drivingLimit.warningTitle'),
      t('drivingLimit.warningBody', { minutes, hours }),
    );
  }
  if (limit.seconds_until_limit != null && limit.seconds_until_limit > 0) {
    await schedule(
      DRIVING_LIMIT_REACHED_NOTIFICATION_ID,
      limit.seconds_until_limit,
      t('drivingLimit.reachedTitle'),
      t('drivingLimit.reachedBody', { hours }),
    );
  }
};

// Syncs run one after another: a poll's sync still awaiting its cancel must
// not schedule after a later stop-driving sync has already cancelled.
let syncQueue: Promise<void> = Promise.resolve();

/**
 * Bring the scheduled notifications in line with the latest server status.
 * Only future crossings are scheduled; a threshold already passed is shown
 * in-app instead, so a refresh never re-notifies the same state. Passing no
 * status (or not driving) only cancels.
 */
export const syncDrivingLimitNotifications = (
  limit: DrivingLimitStatus | null | undefined,
  isDriving: boolean,
  t: Translate,
  language?: string,
): Promise<void> => {
  if (Platform.OS === 'web') return Promise.resolve();
  syncQueue = syncQueue.then(() => applyStatus(limit, isDriving, t, language)).catch(() => undefined);
  return syncQueue;
};
